// Courtesy Tab Component with Branch Inventory
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Gift, Coffee, Trash2, X, Minus, Plus, Check, Truck, AlertTriangle, RefreshCw, Package, History } from 'lucide-react';
import { API } from './constants';

export function CourtesyTab({
  supplies,
  courtesyCart,
  addToCourtesy,
  updateCourtesyQuantity,
  removeFromCourtesy,
  clearCourtesy,
  processCourtesy,
  isProcessing,
  branchId,
  getAuthHeaders,
  userRole,
}) {
  const [branchInventory, setBranchInventory] = useState([]);
  const [masterStock, setMasterStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [selectedSupply, setSelectedSupply] = useState('');
  const [transferQuantity, setTransferQuantity] = useState(1);

  const canManageInventory = ['master_admin', 'admin'].includes(userRole);

  useEffect(() => {
    if (branchId) {
      fetchBranchSupplyInventory();
      fetchMasterSupplyStock();
    }
  }, [branchId]);

  const fetchBranchSupplyInventory = async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/supplies/branch/${branchId}`, getAuthHeaders());
      setBranchInventory(response.data);
    } catch (error) {
      // error handled
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterSupplyStock = async () => {
    try {
      const response = await axios.get(`${API}/supplies/master-stock`, getAuthHeaders());
      setMasterStock(response.data);
    } catch (error) {
      // error handled
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await axios.get(`${API}/supplies/transfers?branch_id=${branchId}&limit=20`, getAuthHeaders());
      setTransferHistory(response.data);
    } catch (error) {
      // error handled
    }
  };

  const handleTransfer = async () => {
    if (!selectedSupply || transferQuantity <= 0) {
      toast.error('Selecciona un insumo y cantidad válida');
      return;
    }

    try {
      const response = await axios.post(`${API}/supplies/transfer`, {
        supply_id: selectedSupply,
        branch_id: branchId,
        quantity: transferQuantity
      }, getAuthHeaders());

      toast.success(response.data.message);
      setShowTransferDialog(false);
      setSelectedSupply('');
      setTransferQuantity(1);
      fetchBranchSupplyInventory();
      fetchMasterSupplyStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al transferir insumo');
    }
  };

  const handleAdjust = async (inventoryId, delta) => {
    try {
      await axios.put(`${API}/supplies/branch-inventory/${inventoryId}/adjust`, {
        quantity: delta
      }, getAuthHeaders());
      
      toast.success(`Stock ${delta > 0 ? 'aumentado' : 'reducido'}`);
      fetchBranchSupplyInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar inventario');
    }
  };

  const openHistoryDialog = () => {
    fetchTransferHistory();
    setShowHistoryDialog(true);
  };

  // Get branch stock for a supply
  const getBranchStock = (supplyId) => {
    const item = branchInventory.find(i => i.supply_id === supplyId);
    return item ? item.stock : 0;
  };

  const lowStockItems = branchInventory.filter(item => item.stock <= (item.min_stock || 5));
  const selectedSupplyData = masterStock.find(s => s.id === selectedSupply);

  // Modified add to courtesy that checks branch inventory
  const handleAddToCourtesy = (supply) => {
    const branchStock = getBranchStock(supply.id);
    if (branchStock <= 0) {
      toast.error(`No hay stock de "${supply.name}" en esta sucursal. Transfiere desde el almacén.`);
      return;
    }
    addToCourtesy({...supply, branch_stock: branchStock});
  };

  return (
    <div className="flex gap-6" data-testid="courtesy-tab">
      {/* Main Content - Supplies Grid with Inventory */}
      <div className="flex-1 space-y-4">
        {/* Header Actions */}
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-2">
            <Coffee className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Insumos de Sucursal</h2>
            <Badge variant="outline" className="text-zinc-400 border-zinc-700">
              {branchInventory.length} items
            </Badge>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchBranchSupplyInventory}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={openHistoryDialog}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <History className="w-4 h-4 mr-2" />
              Historial
            </Button>
            {canManageInventory && (
              <Button 
                size="sm" 
                onClick={() => setShowTransferDialog(true)}
                className="bg-emerald-500 text-black hover:bg-emerald-400"
                data-testid="supply-transfer-btn"
              >
                <Truck className="w-4 h-4 mr-2" />
                Transferir Insumos
              </Button>
            )}
          </div>
        </div>

        {/* Low Stock Alert */}
        {lowStockItems.length > 0 && (
          <Card className="bg-orange-950/30 border-orange-900 p-4">
            <div className="flex items-center gap-2 text-orange-400 mb-3">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-semibold">Stock Bajo ({lowStockItems.length})</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {lowStockItems.map(item => (
                <Badge key={item.id} className="bg-orange-900/50 text-orange-300 text-sm">
                  {item.supply_name}: {item.stock} {item.unit}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Supplies Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-emerald-400" />
          </div>
        ) : branchInventory.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800 p-8 text-center">
            <Package className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
            <p className="text-zinc-400 mb-2">No hay insumos en esta sucursal</p>
            <p className="text-zinc-600 text-sm mb-4">Transfiere insumos desde el almacén central</p>
            {canManageInventory && (
              <Button 
                className="bg-emerald-500 text-black hover:bg-emerald-400"
                onClick={() => setShowTransferDialog(true)}
              >
                <Truck className="w-4 h-4 mr-2" />
                Transferir Insumos
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {branchInventory.map((item) => (
              <Card 
                key={item.id} 
                className={`bg-zinc-900 border-zinc-800 p-3 cursor-pointer hover:border-emerald-500/50 transition-colors ${
                  item.stock <= (item.min_stock || 5) ? 'border-orange-900/50' : ''
                }`}
                onClick={() => handleAddToCourtesy({
                  id: item.supply_id,
                  name: item.supply_name,
                  stock: item.stock,
                  unit: item.unit
                })}
                data-testid={`supply-item-${item.supply_id}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white text-sm truncate">{item.supply_name}</h3>
                    <p className="text-xs text-zinc-500">{item.unit}</p>
                  </div>
                  {item.stock <= (item.min_stock || 5) && (
                    <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xl font-bold text-emerald-400">{item.stock}</p>
                    <p className="text-xs text-zinc-600">Mín: {item.min_stock || 5}</p>
                  </div>
                  
                  {canManageInventory && (
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        size="icon" 
                        variant="outline"
                        className="h-7 w-7 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        onClick={() => handleAdjust(item.id, -1)}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="outline"
                        className="h-7 w-7 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                        onClick={() => handleAdjust(item.id, 1)}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Courtesy Cart */}
      <div className="w-80 bg-zinc-900/50 border border-zinc-800 rounded-sm flex flex-col">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-emerald-400" />
            <h3 className="font-heading text-white">Lista de Cortesías</h3>
          </div>
          {courtesyCart.length > 0 && (
            <button onClick={clearCourtesy} className="text-zinc-400 hover:text-red-500">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <ScrollArea className="flex-1 p-4">
          {courtesyCart.length === 0 ? (
            <div className="text-center py-8">
              <Gift className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Sin cortesías</p>
              <p className="text-zinc-600 text-xs mt-1">Click en un insumo para agregar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {courtesyCart.map((item, index) => (
                <div key={`${item.supply_id}-${index}`} className="bg-zinc-800/50 border border-zinc-800 rounded-sm p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium">{item.name}</p>
                      <p className="text-emerald-400/70 text-xs">
                        Stock: {getBranchStock(item.supply_id)} {item.unit}
                      </p>
                    </div>
                    <button onClick={() => removeFromCourtesy(index)} className="text-zinc-500 hover:text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateCourtesyQuantity(index, -1)}
                        className="w-7 h-7 rounded-sm bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateCourtesyQuantity(index, 1)}
                        className="w-7 h-7 rounded-sm bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="text-emerald-400 text-sm font-medium">{item.quantity} {item.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {courtesyCart.length > 0 && (
          <div className="p-4 border-t border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <span className="text-zinc-400 text-sm">Total insumos:</span>
              <span className="text-emerald-400 font-semibold">
                {courtesyCart.reduce((sum, item) => sum + item.quantity, 0)} unidades
              </span>
            </div>
            <Button
              onClick={processCourtesy}
              disabled={isProcessing}
              className="w-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-semibold uppercase tracking-wide"
              data-testid="pos-process-courtesy-btn"
            >
              {isProcessing ? 'Procesando...' : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Registrar Cortesías
                </>
              )}
            </Button>
            <p className="text-xs text-zinc-500 text-center mt-2">
              Se descontará del inventario de sucursal
            </p>
          </div>
        )}
      </div>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-h-[90vh] overflow-y-auto" data-testid="supply-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-emerald-400" />
              Transferir Insumos a Sucursal
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Insumo</label>
              <Select value={selectedSupply} onValueChange={setSelectedSupply}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Seleccionar insumo" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {masterStock.filter(s => s.stock > 0).map(supply => (
                    <SelectItem key={supply.id} value={supply.id} className="text-white hover:bg-zinc-700">
                      {supply.name} (Stock: {supply.stock} {supply.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm text-zinc-400 mb-2 block">Cantidad</label>
              <div className="flex items-center gap-3">
                <Button 
                  variant="outline" 
                  size="icon"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  onClick={() => setTransferQuantity(Math.max(1, transferQuantity - 1))}
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <Input 
                  type="number" 
                  value={transferQuantity}
                  onChange={(e) => setTransferQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="bg-zinc-800 border-zinc-700 text-white text-center w-20"
                  min="1"
                />
                <Button 
                  variant="outline" 
                  size="icon"
                  className="border-zinc-700 text-white hover:bg-zinc-800"
                  onClick={() => setTransferQuantity(transferQuantity + 1)}
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {selectedSupplyData && (
                <p className="text-xs text-zinc-500 mt-1">
                  Disponible en almacén: {selectedSupplyData.stock} {selectedSupplyData.unit}
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)} className="border-zinc-700 text-zinc-300">
              Cancelar
            </Button>
            <Button onClick={handleTransfer} className="bg-emerald-500 text-black hover:bg-emerald-400" disabled={!selectedSupply}>
              Transferir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-emerald-400" />
              Historial de Transferencias
            </DialogTitle>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto space-y-2 py-4">
            {transferHistory.length === 0 ? (
              <p className="text-center text-zinc-500 py-8">No hay transferencias registradas</p>
            ) : (
              transferHistory.map(transfer => (
                <Card key={transfer.id} className="bg-zinc-800 border-zinc-700 p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-white">{transfer.supply_name}</p>
                      <p className="text-sm text-zinc-400">
                        {transfer.transfer_type === 'adjustment' ? 'Ajuste' : 'Transferencia'}: {transfer.quantity > 0 ? '+' : ''}{transfer.quantity} {transfer.unit}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-zinc-500">{new Date(transfer.created_at).toLocaleDateString('es-MX')}</p>
                      <p className="text-xs text-zinc-600">{transfer.transferred_by_name}</p>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
